import { useState, useEffect, useCallback } from 'react';
import { cn } from '@/lib/utils';
import { Search, UserPlus, Bell, BellOff, Loader2, Users, Contact, Send, Smartphone, Copy, Check, Vote, MessageCircle, ShoppingBag } from 'lucide-react';
import { Link } from 'react-router-dom';
import { WaveButton } from '@/components/users/WaveButton';
import { supabase } from '@/integrations/supabase/client';
import { useAuth } from '@/hooks/useAuth';
import { useContactSync, DeviceContact } from '@/hooks/useContactSync';
import { Input } from '@/components/ui/input';
import { Button } from '@/components/ui/button';
import { Avatar, AvatarImage, AvatarFallback } from '@/components/ui/avatar';
import { getAvatarUrl } from '@/lib/avatars';
import { Tabs, TabsList, TabsTrigger, TabsContent } from '@/components/ui/tabs';
import { Share } from '@capacitor/share';
import { toast } from 'sonner';
import {
  Sheet,
  SheetContent,
  SheetHeader,
  SheetTitle,
  SheetDescription,
} from '@/components/ui/sheet';

interface UserProfile {
  user_id: string;
  display_name: string | null;
  avatar_url: string | null;
  bio: string | null;
}

interface FindUsersSheetProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
}

function SearchTab({ 
  user, 
  onOpenChange 
}: { 
  user: { id: string } | null; 
  onOpenChange: (open: boolean) => void;
}) {
  const [searchQuery, setSearchQuery] = useState('');
  const [users, setUsers] = useState<UserProfile[]>([]);
  const [loading, setLoading] = useState(false);
  const [followingIds, setFollowingIds] = useState<Set<string>>(new Set());
  const [followLoadingId, setFollowLoadingId] = useState<string | null>(null);

  // Fetch users the current user is following
  useEffect(() => {
    if (!user) return;

    const fetchFollowing = async () => {
      const { data } = await supabase
        .from('follows')
        .select('following_id')
        .eq('follower_id', user.id);
      
      if (data) {
        setFollowingIds(new Set(data.map(f => f.following_id)));
      }
    };

    fetchFollowing();
  }, [user, open]);

  // Search users when query changes - prioritize mutual followers
  useEffect(() => {
    const searchUsers = async () => {
      setLoading(true);
      try {
        if (searchQuery.trim()) {
          // When searching, just do a simple search
          let query = supabase
            .from('profiles')
            .select('user_id, display_name, avatar_url, bio')
            .ilike('display_name', `%${searchQuery.trim()}%`)
            .limit(20);

          if (user) {
            query = query.neq('user_id', user.id);
          }

          const { data, error } = await query;
          if (error) throw error;
          setUsers(data || []);
        } else if (user) {
          // No search query: prioritize friends-of-friends
          // 1. Get who I follow
          const { data: myFollowing } = await supabase
            .from('follows')
            .select('following_id')
            .eq('follower_id', user.id);
          
          const myFollowingIds = myFollowing?.map(f => f.following_id) || [];

          // 2. Get who my follows follow (friends-of-friends)
          let mutualSuggestionIds: string[] = [];
          if (myFollowingIds.length > 0) {
            const { data: fofData } = await supabase
              .from('follows')
              .select('following_id')
              .in('follower_id', myFollowingIds)
              .not('following_id', 'eq', user.id);

            if (fofData) {
              // Count occurrences to rank by number of mutual connections
              const countMap = new Map<string, number>();
              for (const f of fofData) {
                if (!myFollowingIds.includes(f.following_id)) {
                  countMap.set(f.following_id, (countMap.get(f.following_id) || 0) + 1);
                }
              }
              // Sort by mutual count descending
              mutualSuggestionIds = [...countMap.entries()]
                .sort((a, b) => b[1] - a[1])
                .map(([id]) => id)
                .slice(0, 15);
            }
          }

          // 3. Fetch mutual suggestion profiles
          let mutualProfiles: UserProfile[] = [];
          if (mutualSuggestionIds.length > 0) {
            const { data } = await supabase
              .from('profiles')
              .select('user_id, display_name, avatar_url, bio')
              .in('user_id', mutualSuggestionIds);
            
            if (data) {
              // Preserve the ranked order
              mutualProfiles = mutualSuggestionIds
                .map(id => data.find(p => p.user_id === id))
                .filter(Boolean) as UserProfile[];
            }
          }

          // 4. Fill remaining slots with other users (excluding already shown + already followed)
          const excludeIds = [user.id, ...mutualSuggestionIds, ...myFollowingIds];
          const remainingSlots = 20 - mutualProfiles.length;
          
          let otherProfiles: UserProfile[] = [];
          if (remainingSlots > 0) {
            let query = supabase
              .from('profiles')
              .select('user_id, display_name, avatar_url, bio')
              .not('user_id', 'in', `(${excludeIds.join(',')})`)
              .order('created_at', { ascending: false })
              .limit(remainingSlots);

            const { data } = await query;
            otherProfiles = (data || []) as UserProfile[];
          }

          setUsers([...mutualProfiles, ...otherProfiles]);
        } else {
          // Not logged in
          const { data, error } = await supabase
            .from('profiles')
            .select('user_id, display_name, avatar_url, bio')
            .order('created_at', { ascending: false })
            .limit(20);
          if (error) throw error;
          setUsers(data || []);
        }
      } catch (error) {
        console.error('Error searching users:', error);
      } finally {
        setLoading(false);
      }
    };

    const debounce = setTimeout(searchUsers, 300);
    return () => clearTimeout(debounce);
  }, [searchQuery, open, user]);

  const toggleFollow = async (targetUserId: string) => {
    if (!user) return;

    setFollowLoadingId(targetUserId);
    try {
      const isFollowing = followingIds.has(targetUserId);
      
      if (isFollowing) {
        await supabase
          .from('follows')
          .delete()
          .eq('follower_id', user.id)
          .eq('following_id', targetUserId);
        
        setFollowingIds(prev => {
          const next = new Set(prev);
          next.delete(targetUserId);
          return next;
        });
      } else {
        await supabase
          .from('follows')
          .insert({
            follower_id: user.id,
            following_id: targetUserId,
          });
        
        setFollowingIds(prev => new Set([...prev, targetUserId]));
      }
    } catch (error) {
      console.error('Error toggling follow:', error);
    } finally {
      setFollowLoadingId(null);
    }
  };

  return (
    <div className="flex-1 flex flex-col overflow-hidden min-h-0">
      <div className="px-5 py-3 border-b border-border/30 shrink-0">
        <div className="relative">
          <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-muted-foreground" />
          <Input
            placeholder="Search by name..."
            value={searchQuery}
            onChange={(e) => setSearchQuery(e.target.value)}
            className="pl-9"
          />
        </div>
      </div>

      <div className="flex-1 overflow-y-auto px-3 py-2 min-h-0">
        <InvitePromoSection />
        {!user ? (
          <div className="flex flex-col items-center justify-center py-12 text-center">
            <div className="w-16 h-16 rounded-full bg-primary/10 flex items-center justify-center mb-4">
              <UserPlus className="w-8 h-8 text-primary" />
            </div>
            <p className="text-muted-foreground text-sm">
              Sign in to find and follow users
            </p>
          </div>
        ) : loading ? (
          <div className="flex items-center justify-center py-12">
            <Loader2 className="w-6 h-6 animate-spin text-muted-foreground" />
          </div>
        ) : users.length === 0 ? (
          <div className="flex flex-col items-center justify-center py-12 text-center">
            <div className="w-16 h-16 rounded-full bg-muted flex items-center justify-center mb-4">
              <Users className="w-8 h-8 text-muted-foreground" />
            </div>
            <p className="text-muted-foreground text-sm">
              {searchQuery ? 'No users found' : 'No users to show yet'}
            </p>
          </div>
        ) : (
          <div className="space-y-1">
            {users.map((profile) => {
              const isFollowing = followingIds.has(profile.user_id);
              const isLoading = followLoadingId === profile.user_id;
              
              return (
                <div
                  key={profile.user_id}
                  className="flex items-center gap-3 p-3 rounded-xl hover:bg-muted/50 transition-colors"
                >
                  <Link 
                    to={`/user/${profile.user_id}`}
                    onClick={() => onOpenChange(false)}
                  >
                    <Avatar className="w-11 h-11 ring-2 ring-border/30">
                      <AvatarImage src={getAvatarUrl(profile.avatar_url, profile.user_id)} />
                      <AvatarFallback className="bg-muted" />
                    </Avatar>
                  </Link>
                  
                  <div className="flex-1 min-w-0">
                    <Link 
                      to={`/user/${profile.user_id}`}
                      onClick={() => onOpenChange(false)}
                      className="hover:underline"
                    >
                      <p className="font-medium text-sm text-foreground truncate">
                        {profile.display_name || 'Anonymous'}
                      </p>
                    </Link>
                    {profile.bio && (
                      <p className="text-xs text-muted-foreground truncate">
                        {profile.bio}
                      </p>
                    )}
                  </div>
                  
                  <div className="flex items-center gap-1.5">
                    <WaveButton 
                      recipientId={profile.user_id} 
                      recipientName={profile.display_name || undefined}
                      size="sm"
                    />
                    <Button
                      onClick={() => toggleFollow(profile.user_id)}
                      disabled={isLoading}
                      variant={isFollowing ? "outline" : "default"}
                      size="sm"
                      className={cn(
                        "gap-1.5 shrink-0 min-w-[90px] justify-center",
                        isFollowing && "border-muted-foreground/30 text-muted-foreground"
                      )}
                    >
                      {isLoading ? (
                        <Loader2 className="w-3.5 h-3.5 animate-spin" />
                      ) : isFollowing ? (
                        <>
                          <BellOff className="w-3.5 h-3.5" />
                          <span>Following</span>
                        </>
                      ) : (
                        <>
                          <Bell className="w-3.5 h-3.5" />
                          <span>Follow</span>
                        </>
                      )}
                    </Button>
                  </div>
                </div>
              );
            })}
          </div>
        )}
      </div>
    </div>
  );
}

function ContactsTab() {
  const { 
    contacts, 
    loading, 
    error, 
    permissionStatus, 
    isNative, 
    requestPermissionAndFetch 
  } = useContactSync();
  const [invitingId, setInvitingId] = useState<string | null>(null);
  const [hasRequested, setHasRequested] = useState(false);

  const handleRequestAccess = useCallback(async () => {
    setHasRequested(true);
    await requestPermissionAndFetch();
  }, [requestPermissionAndFetch]);

  const handleInvite = async (contact: DeviceContact) => {
    setInvitingId(contact.id);
    try {
      await Share.share({
        title: 'Join me on WNTD',
        text: `Hey ${contact.name.split(' ')[0]}! Join me on WNTD – we can create private polls to vote on outfits together and build shared collections to chat about what we're eyeing. It's like a group chat for shopping decisions! 🛍️`,
        url: 'https://seyu.lovable.app',
        dialogTitle: `Invite ${contact.name}`,
      });
    } catch (err) {
      // User cancelled or share failed
      console.log('Share cancelled or failed:', err);
    } finally {
      setInvitingId(null);
    }
  };

  // Web fallback
  if (!isNative) {
    return (
      <div className="flex-1 flex flex-col items-center justify-center px-6 py-12 text-center">
        <div className="w-20 h-20 rounded-full bg-primary/10 flex items-center justify-center mb-5">
          <Smartphone className="w-10 h-10 text-primary" />
        </div>
        <h3 className="font-semibold text-foreground mb-2">Open on your phone</h3>
        <p className="text-sm text-muted-foreground max-w-[280px]">
          Contact sync is available in the mobile app. Open Want on your phone to invite friends from your contacts.
        </p>
      </div>
    );
  }

  // Permission not yet requested
  if (!hasRequested && permissionStatus?.contacts !== 'granted') {
    return (
      <div className="flex-1 flex flex-col items-center justify-center px-6 py-12 text-center">
        <div className="w-20 h-20 rounded-full bg-primary/10 flex items-center justify-center mb-5">
          <Contact className="w-10 h-10 text-primary" />
        </div>
        <h3 className="font-semibold text-foreground mb-2">Find friends in your contacts</h3>
        <p className="text-sm text-muted-foreground max-w-[280px] mb-6">
          We'll check your contacts to help you invite friends. Your contacts stay on your device and are never uploaded.
        </p>
        <Button onClick={handleRequestAccess} className="gap-2">
          <Contact className="w-4 h-4" />
          Allow Contact Access
        </Button>
      </div>
    );
  }

  // Loading state
  if (loading) {
    return (
      <div className="flex-1 flex items-center justify-center py-12">
        <Loader2 className="w-6 h-6 animate-spin text-muted-foreground" />
      </div>
    );
  }

  // Error state
  if (error) {
    return (
      <div className="flex-1 flex flex-col items-center justify-center px-6 py-12 text-center">
        <div className="w-16 h-16 rounded-full bg-destructive/10 flex items-center justify-center mb-4">
          <Contact className="w-8 h-8 text-destructive" />
        </div>
        <p className="text-sm text-muted-foreground">{error}</p>
        <Button variant="outline" onClick={handleRequestAccess} className="mt-4">
          Try Again
        </Button>
      </div>
    );
  }

  // No contacts found
  if (contacts.length === 0) {
    return (
      <div className="flex-1 flex flex-col items-center justify-center px-6 py-12 text-center">
        <div className="w-16 h-16 rounded-full bg-muted flex items-center justify-center mb-4">
          <Contact className="w-8 h-8 text-muted-foreground" />
        </div>
        <p className="text-sm text-muted-foreground">No contacts with phone numbers found</p>
      </div>
    );
  }

  // Contacts list
  return (
    <div className="flex-1 overflow-y-auto px-3 py-2">
      <div className="space-y-1">
        {contacts.map((contact) => {
          const isInviting = invitingId === contact.id;
          
          return (
            <div
              key={contact.id}
              className="flex items-center gap-3 p-3 rounded-xl hover:bg-muted/50 transition-colors"
            >
              <div className="w-11 h-11 rounded-full bg-muted flex items-center justify-center ring-2 ring-border/30">
                <span className="text-sm font-medium text-muted-foreground">
                  {contact.initials}
                </span>
              </div>
              
              <div className="flex-1 min-w-0">
                <p className="font-medium text-sm text-foreground truncate">
                  {contact.name}
                </p>
                <p className="text-xs text-muted-foreground truncate">
                  {contact.phoneNumbers[0]}
                </p>
              </div>
              
              <Button
                onClick={() => handleInvite(contact)}
                disabled={isInviting}
                size="sm"
                className="gap-1.5 shrink-0"
              >
                {isInviting ? (
                  <Loader2 className="w-3.5 h-3.5 animate-spin" />
                ) : (
                  <>
                    <Send className="w-3.5 h-3.5" />
                    <span>Invite</span>
                  </>
                )}
              </Button>
            </div>
          );
        })}
      </div>
    </div>
  );
}

function InvitePromoSection() {
  const [copied, setCopied] = useState(false);
  const inviteLink = 'https://seyu.lovable.app';

  const handleCopyLink = async () => {
    try {
      await navigator.clipboard.writeText(inviteLink);
      setCopied(true);
      toast.success('Link copied!');
      setTimeout(() => setCopied(false), 2000);
    } catch (err) {
      console.error('Failed to copy:', err);
    }
  };

  return (
    <div className="mx-2 mb-3 p-3 rounded-xl bg-gradient-to-br from-primary/10 via-accent/5 to-primary/5 border border-primary/20">
      <h3 className="text-sm font-semibold text-foreground mb-2">
        Shop together with friends 🛍️
      </h3>
      <p className="text-xs text-muted-foreground mb-4">
        Get votes from your mates on outfits, chat about potential buys, and build shared collections together.
      </p>
      
      {/* Feature pills */}
      <div className="flex flex-wrap gap-2 mb-4">
        <span className="inline-flex items-center gap-1 px-2 py-1 rounded-full bg-background/60 text-xs text-muted-foreground">
          <Vote className="w-3 h-3" />
          Private polls
        </span>
        <span className="inline-flex items-center gap-1 px-2 py-1 rounded-full bg-background/60 text-xs text-muted-foreground">
          <MessageCircle className="w-3 h-3" />
          Chat on collections
        </span>
        <span className="inline-flex items-center gap-1 px-2 py-1 rounded-full bg-background/60 text-xs text-muted-foreground">
          <ShoppingBag className="w-3 h-3" />
          Shop together
        </span>
      </div>

      {/* Copyable invite link */}
      <button
        onClick={handleCopyLink}
        className="w-full flex items-center gap-2 p-2.5 rounded-lg bg-background border border-border/50 active:scale-[0.98] transition-all"
      >
        <span className="flex-1 text-left text-xs text-muted-foreground truncate font-mono">
          {inviteLink}
        </span>
        <span className={cn(
          "shrink-0 flex items-center gap-1 text-xs font-medium px-2 py-1 rounded-md transition-colors",
          copied 
            ? "bg-green-500/10 text-green-600" 
            : "bg-primary/10 text-primary"
        )}>
          {copied ? (
            <>
              <Check className="w-3 h-3" />
              Copied
            </>
          ) : (
            <>
              <Copy className="w-3 h-3" />
              Copy
            </>
          )}
        </span>
      </button>
    </div>
  );
}

export function FindUsersSheet({ open, onOpenChange }: FindUsersSheetProps) {
  const { user } = useAuth();

  return (
    <Sheet open={open} onOpenChange={onOpenChange}>
      <SheetContent side="left" className="w-full sm:w-96 p-0 flex flex-col pt-safe">
        <SheetHeader className="px-5 pt-4 pb-3 border-b border-border/30 shrink-0">
          <SheetTitle className="flex items-center gap-2">
            <Users className="w-5 h-5 text-primary" />
            Add Friends
          </SheetTitle>
          <SheetDescription className="text-left">
            Vote on outfits together with private polls, or build shared collections and chat about what you're eyeing.
          </SheetDescription>
        </SheetHeader>

        <Tabs defaultValue="search" className="flex-1 flex flex-col overflow-hidden min-h-0">
          <TabsList className="mx-5 mt-2 grid w-auto grid-cols-2 shrink-0">
            <TabsTrigger value="search" className="gap-1.5">
              <Search className="w-4 h-4" />
              Search
            </TabsTrigger>
            <TabsTrigger value="contacts" className="gap-1.5">
              <Contact className="w-4 h-4" />
              Contacts
            </TabsTrigger>
          </TabsList>

          <TabsContent value="search" className="flex-1 flex flex-col overflow-hidden min-h-0 mt-0 data-[state=inactive]:hidden">
            <SearchTab user={user} onOpenChange={onOpenChange} />
          </TabsContent>

          <TabsContent value="contacts" className="flex-1 flex flex-col overflow-hidden min-h-0 mt-0 data-[state=inactive]:hidden">
            <ContactsTab />
          </TabsContent>
        </Tabs>

        {/* Info footer */}
        <div className="px-5 py-3 border-t border-border/30 bg-muted/30 shrink-0 pb-safe">
          <div className="flex items-start gap-3">
            <div className="p-2 bg-primary/10 rounded-lg shrink-0">
              <Bell className="w-4 h-4 text-primary" />
            </div>
            <div>
              <p className="text-xs font-medium text-foreground">Poll notifications only</p>
              <p className="text-xs text-muted-foreground">
                Following a user notifies you of their new polls. Their saved items stay private.
              </p>
            </div>
          </div>
        </div>
      </SheetContent>
    </Sheet>
  );
}
